import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { QuestionType, IQuestionListItem, IQuestionListItemQuestionAnswer } from '@/types';
import Audio from '@/components/audio';
import VideoPlay from '@/components/video';
import QuestionPlaza from './question-plaza';
import QuestionActions from './Actions';
import './index.less';
import DOMPurify from 'dompurify';
import { getQuestionType, popUpComprehensiveQuestionTitle, transformDurationTime, uuid } from '@/utils';
import VocabularyNotebook from './vocabulary-notebook';
import { ClozeAnalysis } from './cloze-test';
import ShortAnswerAnalysis from './short-answer-view/ShortAnswerAnalysis';
import { useAppStore } from '@/store';
import { observer } from 'mobx-react';
import { RightOutlined } from '@ant-design/icons';
import globalStore from '@/store/global';
import { bindEvents } from '@/utils/sensors';
import { ExamPointsRightArrowIcon, PushQuestionIcon } from '@/svgs';
import { useAppContext } from '@/context/app-contex';
import { getComprehendByAnalogyId, getOldComprehendByAnalogyId } from '@/apis';
import { environment } from '@gaodun.com/gtools';
import { message } from 'antd';

interface IProps {
    questionItem: IQuestionListItem;
    paperDataId?: number; // 试卷id
    actionNode?: ReactNode; // 工具栏
    parentId?: number; // 父id
    removeMyNote?: boolean; // 是否移除 我的笔记
    removeMyAnswer?: boolean; // 是否移除我的答案
    removeQuestionUseTime?: boolean; // 是否移除 答题用时
    isPredictScore?: boolean; // 是否预测分相关的题目
}

const QuestionAnalysis: React.FC<IProps> = props => {
    const { questionItem, paperDataId, actionNode, parentId, removeMyNote, removeMyAnswer, removeQuestionUseTime } = props;
    const { selectList, questionId, projectId, subjectId } = questionItem;

    const { pushQuestionCallback, sourceFromType, businessParam } = useAppContext();

    const { commonStore } = useAppStore();
    const { pastPaperList, isAllowCopy, exerciseType } = commonStore.common;

    /**
     * 兼容老题库没有旧选项多选题
     * @param optionNum  选项个数
     * @param multipleRightAnswerList 综合题正确答案
     * @param multipleUserAnswerList 用户答案
     */
    const getMultipleOldOptionItem = (optionNum: number, multipleRightAnswerList: string[], multipleUserAnswerList: string[]) => {
        const list: JSX.Element[] = [];
        for (let i = 0; i < optionNum; i++) {
            const option = String.fromCharCode(65 + i);
            list.push(
                <div
                    className={classNames(
                        'sub-tiku__question-select-item',
                        'sub-tiku__analysis-select-item',
                        'sub-tiku__multiply-select-item',
                        {
                            'sub-tiku__questionRight': multipleRightAnswerList.includes(option) && multipleUserAnswerList.includes(option),
                            'sub-tiku__questionError': !multipleRightAnswerList.includes(option) && multipleUserAnswerList.includes(option),
                        }
                    )}
                    key={uuid()}
                >
                    <span className="sub-tiku__question-option">{option}</span>
                    <span className="sub-tiku__question-option-content">{option}</span>
                </div>
            );
        }
        return list;
    };

    /**
     * 兼容老题库单选题
     * @param optionNum 选项个数
     * @param singleRightAnswer  正确答案
     * @param singleUserAnswer 用户答案
     */
    const getSingleOldOptionItem = (optionNum: number, singleRightAnswer: string | number, singleUserAnswer: string) => {
        const list: JSX.Element[] = [];
        for (let i = 0; i < optionNum; i++) {
            const option = String.fromCharCode(65 + i);
            list.push(
                <div
                    className={classNames('sub-tiku__question-select-item', 'sub-tiku__analysis-select-item', {
                        'sub-tiku__questionRight': singleRightAnswer === option,
                        'sub-tiku__questionError': singleUserAnswer !== singleRightAnswer && singleUserAnswer === option,
                    })}
                    key={uuid()}
                >
                    <span className="sub-tiku__question-option">{option}</span>
                    <span className="sub-tiku__question-option-content">{option}</span>
                </div>
            );
        }
        return list;
    };

    // 不同题型选项
    const getDiffQuestionItem = () => {
        // 单选变量
        const singleRightAnswer = questionItem.questionAnswer?.answer || '';
        const singleUserAnswer = questionItem.userAnswer?.userAnswer || '';

        // 多选变量
        const multipleRightAnswerList = String(questionItem.questionAnswer?.answer || '').split(',');
        const multipleUserAnswerList = String(questionItem.userAnswer?.userAnswer || '').split(',');

        // 判断变量
        const judgeAnswer = questionItem.questionAnswer?.answer;
        const userAnswer = questionItem.userAnswer?.userAnswer;

        switch (questionItem.questionType) {
            case QuestionType.single:
                return selectList?.length ? (
                    selectList.map(item => (
                        <div
                            className={classNames('sub-tiku__question-select-item', 'sub-tiku__analysis-select-item', {
                                'sub-tiku__questionRight': singleRightAnswer === item.option && !removeMyAnswer,
                                'sub-tiku__questionError':
                                    singleUserAnswer !== singleRightAnswer && singleUserAnswer === item.option && !removeMyAnswer,
                            })}
                            key={item.id || uuid()}
                        >
                            <span className="sub-tiku__question-option">{item.option}</span>
                            <span
                                className="sub-tiku__question-option-content"
                                dangerouslySetInnerHTML={{
                                    __html: item.content,
                                }}
                            />
                        </div>
                    ))
                ) : (
                    <>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: questionItem.option,
                            }}
                            style={{ marginBottom: 20 }}
                        />
                        {getSingleOldOptionItem(questionItem.optionNum, singleRightAnswer, String(singleUserAnswer))}
                    </>
                );
            case QuestionType.multiply:
            case QuestionType.noDirection:
                return selectList?.length ? (
                    selectList.map(item => (
                        <div
                            className={classNames(
                                'sub-tiku__question-select-item',
                                'sub-tiku__analysis-select-item',
                                'sub-tiku__multiply-select-item',
                                {
                                    'sub-tiku__questionRight':
                                        multipleRightAnswerList.includes(item.option) &&
                                        multipleUserAnswerList.includes(item.option) &&
                                        !removeMyAnswer,
                                    'sub-tiku__questionError':
                                        !multipleRightAnswerList.includes(item.option) &&
                                        multipleUserAnswerList.includes(item.option) &&
                                        !removeMyAnswer,
                                    'sub-tiku__questionError2':
                                        multipleRightAnswerList.includes(item.option) &&
                                        !multipleUserAnswerList.includes(item.option) &&
                                        !removeMyAnswer,
                                }
                            )}
                            key={item.id || uuid()}
                        >
                            <span className="sub-tiku__question-option">{item.option}</span>
                            <span
                                className="sub-tiku__question-option-content"
                                dangerouslySetInnerHTML={{
                                    __html: item.content,
                                }}
                            />
                        </div>
                    ))
                ) : (
                    <>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: questionItem.option,
                            }}
                            style={{ marginBottom: 20 }}
                        />
                        {getMultipleOldOptionItem(questionItem.optionNum, multipleRightAnswerList, multipleUserAnswerList)}
                    </>
                );
            case QuestionType.judge:
                return (
                    <>
                        <div
                            className={classNames('sub-tiku__question-select-item', 'sub-tiku__analysis-select-item', {
                                'sub-tiku__questionError': judgeAnswer !== userAnswer && userAnswer === '1',
                                'sub-tiku__questionRight': judgeAnswer === '1' && !removeMyAnswer,
                            })}
                        >
                            <span className="sub-tiku__question-option">A</span>
                            <span className="sub-tiku__question-option-content">正确</span>
                        </div>
                        <div
                            className={classNames('sub-tiku__question-select-item', 'sub-tiku__analysis-select-item', {
                                'sub-tiku__questionError': judgeAnswer !== userAnswer && userAnswer === '0',
                                'sub-tiku__questionRight': judgeAnswer === '0' && !removeMyAnswer,
                            })}
                        >
                            <span className="sub-tiku__question-option">B</span>
                            <span className="sub-tiku__question-option-content">错误</span>
                        </div>
                    </>
                );
            default:
                return '';
        }
    };

    const getCorrectAnswer = (questionType: QuestionType, answer?: IQuestionListItemQuestionAnswer) => {
        if (!answer?.answer) {
            return '-';
        }
        // 解析判断正确答案
        if (questionType === QuestionType.judge) {
            return [1, '1'].includes(answer?.answer) ? '正确' : [0, '0'].includes(answer?.answer) ? '错误' : '';
        }
        return <span dangerouslySetInnerHTML={{ __html: String(answer?.answer) }} />;
    };

    const getUserAnswer = (questionType: QuestionType, answer?: IQuestionListItem['userAnswer']) => {
        if (!answer?.userAnswer) {
            return '-';
        }
        if (questionType === QuestionType.judge) {
            return [1, '1'].includes(answer?.userAnswer) ? '正确' : '错误';
        }
        return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(String(answer?.userAnswer)) }} />;
    };

    if (questionItem.questionType === QuestionType.comprehensive) {
        return (
            <div className="sub-tiku__question-container sub-tiku__question-split-line">
                <div className="sub-tiku__question-prefix">
                    {!!questionItem.sortNum && <span className="sub-tiku__question-sort">{questionItem.sortNum}.</span>}
                    <div className="sub-tiku__question-comprehensive-type">材料</div>
                </div>
                <div className="sub-tiku__question-title-content">
                    {questionItem.titleAudio && <Audio key={uuid()} src={questionItem.titleAudio} />}
                    <VocabularyNotebook questionTitle={questionItem.title} questionId={questionId} />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div
                        className="sub-tiku__question-use-btn"
                        onClick={() => {
                            popUpComprehensiveQuestionTitle(questionItem.title, Boolean(isAllowCopy));
                        }}
                    >
                        弹出
                        <img
                            src="http://simg01.gaodunwangxiao.com/uploadfiles/tmp/upload/202407/22/6ef84_20240722112527.png"
                            style={{ marginLeft: 4 }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const makeUserClozeAnswer = () => {
        const userAnswers = [];
        for (let index = 0; index < questionItem.clozeQuestionNum; index++) {
            const key = questionItem.questionAnswer?.clozeAnswers[index].key;
            const userAnswer = questionItem.userAnswer?.userClozeAnswers?.find((answer: { key: any }) => answer.key === key);
            if (index + 1 < questionItem.clozeQuestionNum) {
                userAnswers.push(
                    <span
                        dangerouslySetInnerHTML={{ __html: (userAnswer?.answerContent || '-') + '&nbsp;&nbsp;|&nbsp;&nbsp;' }}
                        key={`${index + 1}`}
                    />
                );
            } else {
                userAnswers.push(<span dangerouslySetInnerHTML={{ __html: userAnswer?.answerContent || '-' }} key={`${index + 1}`} />);
            }
        }
        return userAnswers;
    };

    const handleGotoPredictScoreKnowledgeDetail = (knowledgeId: number, knowledgeTitle: string, examSeasonId: number) => {
        bindEvents(
            'Click_Knowledgelabel',
            {
                name: '点击考点标签',
                project_id: questionItem.projectId,
                subject_id: questionItem.subjectId,
                KnowledgePoint_name: knowledgeTitle,
            },
            80
        );

        setTimeout(() => {
            globalStore.publish('main-app', {
                type: 'gotoExamPointDetail',
                params: {
                    knowledgeId,
                    examSeasonId,
                },
            });
        }, 80);
    };

    const handleGoToPractice = (paperDataLogId: number) => {
        if (pushQuestionCallback) {
            pushQuestionCallback(paperDataLogId);
        } else {
            window.open(
                `https://${environment.getEnv()}tiku.gaodun.com/${projectId}/${subjectId}/examination/exam/practice/${paperDataLogId}?projectId=${projectId}&subjectId=${subjectId}`
            );
        }
    };

    const handleClickPushQuestion = async () => {
        const pushQuestionBusinessParam = businessParam ? { courseId: businessParam.courseId, businessSource: 25 } : undefined;
        const res =
            exerciseType === 115
                ? await getComprehendByAnalogyId({
                      questionId: questionId,
                      projectId,
                      subjectId,
                      sourceFromType: sourceFromType,
                      businessParam: pushQuestionBusinessParam,
                  })
                : await getOldComprehendByAnalogyId({
                      questionId: questionId,
                      sourceFromType: sourceFromType || 0,
                      businessParam: pushQuestionBusinessParam,
                  });
        if (res.status === 0) {
            if (res.result?.paperDataLogId) {
                message.success(`当前分类下符合条件的题目为${res.result.questionTotal}道，开始练习吧 ~`);
                const { paperDataLogId } = res.result;
                setTimeout(() => {
                    handleGoToPractice(paperDataLogId!);
                }, 1500);
                return;
            }
            message.info('暂无题目');
        }
    };

    return (
        <div className="sub-tiku__question-container">
            <div className="sub-tiku__question-header">
                <div className="sub-tiku__question-prefix">
                    {!!questionItem.sortNum && <span className="sub-tiku__question-sort">{questionItem.sortNum}.</span>}
                    <div className="sub-tiku__question-type">{getQuestionType(questionItem.questionType)}</div>
                </div>
                <div className="sub-tiku__question-title-tools">
                    {actionNode || <QuestionActions questionItem={questionItem} parentId={parentId} isAnalysis />}
                </div>
            </div>
            <div className="sub-tiku__question-title-content">
                {questionItem.titleAudio && <Audio key={uuid()} src={questionItem.titleAudio} />}
                {questionItem.questionType === QuestionType.cloze ? (
                    <ClozeAnalysis questionItem={questionItem} />
                ) : (
                    <VocabularyNotebook questionTitle={questionItem.title} questionId={questionId} />
                )}
            </div>
            <div style={{ marginTop: 24 }}>{getDiffQuestionItem()}</div>

            {[QuestionType.cloze].includes(questionItem.questionType) && (
                <>
                    {!removeMyAnswer && (
                        <div className="sub-tiku__question-right-statics">
                            <div className="sub-tiku__statics-title">我的答案：</div>
                            <div className="sub-tiku__statics-content">{makeUserClozeAnswer()}</div>
                        </div>
                    )}

                    <div className="sub-tiku__question-right-statics">
                        <div className="sub-tiku__statics-title">正确答案：</div>
                        <div
                            className="sub-tiku__statics-content sub-tiku__right"
                            dangerouslySetInnerHTML={{
                                __html: questionItem.questionAnswer?.clozeAnswers.map(item => item.answerContent).join('｜') || '',
                            }}
                        />

                        {!removeQuestionUseTime && (
                            <div style={{ marginTop: 20 }} className="sub-tiku__statics-content">
                                答题用时：
                                <span className="sub-tiku__default">
                                    {questionItem.userAnswer?.costTime ? transformDurationTime(questionItem.userAnswer.costTime) : '-'}
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}
            {[QuestionType.shortAnswer].includes(questionItem.questionType) && (
                <ShortAnswerAnalysis
                    key={`short-answer-${questionItem.questionId}`}
                    removeMyAnswer={removeMyAnswer}
                    removeQuestionUseTime={removeQuestionUseTime}
                    questionItem={questionItem}
                />
            )}

            {[QuestionType.gapFill].includes(questionItem.questionType) && (
                <>
                    {!removeMyAnswer && (
                        <div className="sub-tiku__question-right-statics">
                            <div className="sub-tiku__statics-title">我的答案：</div>
                            <div
                                className="sub-tiku__statics-content"
                                dangerouslySetInnerHTML={{
                                    __html: questionItem.userAnswer?.userAnswer + '' || '-',
                                }}
                            />
                        </div>
                    )}

                    <div className="sub-tiku__question-right-statics">
                        <div className="sub-tiku__statics-title">正确答案：</div>
                        <div
                            className="sub-tiku__statics-content sub-tiku__right"
                            dangerouslySetInnerHTML={{
                                __html: questionItem.questionAnswer?.answer + '' || '',
                            }}
                        />
                        {!removeQuestionUseTime && (
                            <div style={{ marginTop: 20 }} className="sub-tiku__statics-content">
                                答题用时：
                                <span className="sub-tiku__default">
                                    {questionItem.userAnswer?.costTime ? transformDurationTime(questionItem.userAnswer.costTime) : '-'}
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}

            {[QuestionType.single, QuestionType.judge, QuestionType.multiply, QuestionType.noDirection].includes(
                questionItem.questionType
            ) && (
                <div className="sub-tiku__question-answer-statics">
                    {!removeMyAnswer && (
                        <div>
                            我的答案：
                            <span
                                className={classNames('sub-tiku__yourself-answer', {
                                    'sub-tiku__right': questionItem.userAnswer?.userAnswerStatus === 1,
                                    'sub-tiku__error': questionItem.userAnswer?.userAnswerStatus !== 1,
                                })}
                            >
                                {getUserAnswer(questionItem.questionType, questionItem.userAnswer)}
                            </span>
                        </div>
                    )}

                    <div>
                        正确答案：
                        <span className="sub-tiku__yourself-answer sub-tiku__right">
                            {getCorrectAnswer(questionItem.questionType, questionItem?.questionAnswer)}
                        </span>
                    </div>

                    {!removeQuestionUseTime && (
                        <div>
                            答题用时：
                            <span className="sub-tiku__yourself-answer">
                                {questionItem.userAnswer?.costTime ? transformDurationTime(questionItem.userAnswer.costTime) : '-'}
                            </span>
                        </div>
                    )}

                    <div>
                        全站正确率：
                        <span className="sub-tiku__yourself-answer sub-tiku__right">{questionItem.questionCount?.correctRate || '-'}</span>
                    </div>
                </div>
            )}

            <div className="sub-tiku__question-analysis-answer">
                <div className="sub-tiku__analysis-module-title">
                    <div />
                    解析
                </div>
                <div
                    className="sub-tiku__question-analysis-content"
                    dangerouslySetInnerHTML={{
                        __html: questionItem.questionAnswer?.analysis || '',
                    }}
                />
            </div>

            {!!questionItem.questionAnswer?.questionVideo?.sourceId && (
                <div className="sub-tiku__question-analysis-video-audio">
                    <div className="sub-tiku__video-box">
                        <VideoPlay
                            auto={false}
                            key={questionItem.questionId}
                            videoId={questionItem.questionAnswer.questionVideo.sourceId}
                        />
                    </div>
                </div>
            )}

            {!!questionItem.questionAnswer?.audioUrl && (
                <div className="sub-tiku__question-analysis-video-audio">
                    <div className="sub-tiku__analysis-module-title">
                        <div />
                        音频
                    </div>
                    <Audio key={uuid()} src={questionItem.questionAnswer.audioUrl} />
                </div>
            )}
            {!!questionItem.questionAnswer?.translate && (
                <div className="sub-tiku__question-analysis-video-audio">
                    <div className="sub-tiku__analysis-module-title">
                        <div />
                        翻译
                    </div>
                    <div
                        className="sub-tiku__question-analysis-content"
                        dangerouslySetInnerHTML={{
                            __html: questionItem.questionAnswer.translate,
                        }}
                    />
                </div>
            )}

            {exerciseType !== 116 && !!questionItem.knowledgePointList?.length && (
                <div className="sub-tiku__question-analysis-tag">
                    <div className="sub-tiku__analysis-module-title">
                        <div />
                        举一反三
                    </div>
                    <div className="sub-tiku__push-question-btn" onClick={handleClickPushQuestion}>
                        <PushQuestionIcon />
                        练一练
                        <ExamPointsRightArrowIcon />
                    </div>
                </div>
            )}

            {!!questionItem.knowledgePointList?.length && (
                <div className="sub-tiku__question-analysis-tag">
                    <div className="sub-tiku__analysis-module-title">
                        <div />
                        考点标签
                    </div>
                    {questionItem.knowledgePointList.map(item =>
                        props.isPredictScore ? (
                            <div
                                className="sub-tiku__question-tag-item sub-tiku__question-predict-score-knowledge"
                                key={item.id}
                                onClick={() => {
                                    handleGotoPredictScoreKnowledgeDetail(item.id, item.title, item.examSeasonId);
                                }}
                            >
                                {item.title} <RightOutlined />
                            </div>
                        ) : (
                            <div className="sub-tiku__question-tag-item" key={item.id}>
                                {item.title}
                            </div>
                        )
                    )}
                </div>
            )}

            {!!pastPaperList?.length && (
                <div className="sub-tiku__question-analysis-tag">
                    <div className="sub-tiku__analysis-module-title">
                        <div />
                        来源
                    </div>
                    {commonStore.common.pastPaperList?.map(item => (
                        <div className="sub-tiku__question-tag-item" key={item.paperId}>
                            {item.paperName}
                        </div>
                    ))}
                </div>
            )}

            {!removeMyNote && <QuestionPlaza key={questionId} paperDataId={paperDataId} questionItem={questionItem} />}
        </div>
    );
};

export default observer(QuestionAnalysis);
